<template>
  <EntityDrawer :visible="visible" :content="rev" @close="$emit('close')">
    <template v-slot:footer>
      <div class="d-flex">
        <Button
          v-if="!isImplemented"
          block
          class="pl-3 pr-3"
          :text="$t('rev.validation.mark_as_implemented')"
          icon="mdi-rocket-launch"
          :disabled="!permissions.implement"
          @click="handleRevImplementation"
        />
        <v-menu
          v-if="!permissions.implement && !isImplemented"
          open-on-hover
          offset-x
          max-width="200"
          right
        >
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" class="ml-1"> mdi-help-circle-outline </v-icon>
          </template>
          <div class="grey darken-4 px-3 py-5 text-center">
            <p class="text-body-2 white--text">
              {{ $t('validation.no_permission_implementation_content') }}
            </p>
          </div>
        </v-menu>
        <Button
          v-if="isImplemented && !requestValidationInProgress"
          block
          class="pl-3 pr-3"
          icon="mdi-check-decagram-outline"
          :text="$t('rev.validation.request_validation')"
          @click="showRequestModal = true"
        />
        <Button
          v-if="isImplemented && requestValidationInProgress"
          block
          class="pl-3 pr-3"
          icon="mdi-check-decagram-outline"
          :text="$t('common.button.view_request')"
          @click="showRequestViewModal = true"
        />
      </div>
      <RequestValidationModal
        :show="showRequestModal"
        @hideModal="showRequestModal = false"
        @createValidationRequest="handleCreateValidationRequest"
      />

      <RequestViewModal
        :show="showRequestViewModal"
        :requestValidation="requestValidationInProgress"
        @hideModal="showRequestViewModal = false"
        @cancelValidationRequest="handleCancelValidationRequest"
      />
    </template>
  </EntityDrawer>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'

import mixin from '@/mixins/mixin.vue'
import revPermissionsMixin from '@/mixins/revPermissionsMixin'
import trackerMixin from '@/mixins/tracking/trackerMixin'

import EntityDrawer from '@/components/EntityDrawer/EntityDrawer.vue'
import Button from '@/components/UI/Button.vue'
import RequestValidationModal from '@/containers/Modals/RequestValidationModal.vue'
import RequestViewModal from '@/containers/Modals/RequestViewModal.vue'

export default {
  name: 'ValidationDrawer',

  mixins: [mixin, revPermissionsMixin, trackerMixin],

  components: {
    EntityDrawer,
    Button,
    RequestValidationModal,
    RequestViewModal,
  },

  props: {
    rev: {
      type: Object,
      required: true,
    },
    visible: Boolean,
  },

  data() {
    return {
      showRequestModal: false,
      showRequestViewModal: false,
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'account', 'organizationSiteGroups']),

    permissions() {
      return this.getPermissions({
        rev: this.rev,
        account: this.account,
        organizationSiteGroups: this.organizationSiteGroups,
      })
    },

    isImplemented() {
      return Boolean(this.rev?.validations?.implementation?.isValidated)
    },

    requestValidationInProgress() {
      const requestInProgress = this.rev?.validationRequest?.find(
        validationRequest =>
          validationRequest?.status ===
          this.$constants.validationRequestStatus.inProgress
      )

      if (requestInProgress) {
        return {
          ...requestInProgress,
          assignees: this.rev?.requestAssignees ?? [],
          createdBy: this.rev?.requestCreatedBy ?? {},
        }
      } else {
        return null
      }
    },
  },

  methods: {
    ...mapActions({
      setImplementationValidation: 'validations/setImplementationValidation',
      iiCreateValidationRequest: 'improvementIdea/validationRequest',
      iiCancelValidationRequest: 'improvementIdea/updateRequestStatus',
      psCreateValidationRequest: 'problemSolving/validationRequest',
      psCancelValidationRequest: 'problemSolving/updateRequestStatus',
      qaCreateValidationRequest: 'quickAction/validationRequest',
      qaCancelValidationRequest: 'quickAction/updateRequestStatus',
    }),

    async handleRevImplementation() {
      try {
        const revType =
          this.rev.entityType === this.$constants.revTypes.improvementIdea
            ? 'rev'
            : this.rev.entityType.replace('-', '').toLowerCase()
        const payload = {
          revId: this.rev.id,
          organizationId: this.organizationId,
          isValidated: this.isImplemented,
          revType,
          validationId: this.rev?.validations?.implementation?.id,
        }
        await this.setImplementationValidation(payload)
        this.trackValidationsActions({
          action: 'drawer-markAsImplemented',
          revId: this.rev.id,
          revType: revType,
        })
        return this.isImplemented
          ? this.showSuccess(this.$t('rev.rev_implementation_removed'))
          : this.showSuccess(this.$t('rev.rev_implemented'))
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.$emit('implementRev')
        this.$emit('close')
      }
    },

    async handleCreateValidationRequest(assignee) {
      try {
        const payload = {
          organizationId: this.organizationId,
          revId: this.rev.id,
          assignees: _.compact([assignee]),
        }

        switch (this.rev.entityType) {
          case this.$constants.revTypes.improvementIdea:
            await this.iiCreateValidationRequest(payload)
            break
          case this.$constants.revTypes.problemSolving:
            await this.psCreateValidationRequest(payload)
            break
          default:
            await this.qaCreateValidationRequest(payload)
        }
        this.trackValidationsActions({
          action: 'drawer-requestValidation',
          revId: this.rev.id,
          revType: this.revType,
        })

        this.showSuccess(this.$t('rev.validation_request_successfully'))
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.$emit('requestValidation')
        this.$emit('close')
      }
    },

    async handleCancelValidationRequest(payload) {
      try {
        const requestPayload = {
          organizationId: this.organizationId,
          revId: this.rev.id,
          requestId: payload.requestId,
          status: payload.status,
        }
        switch (this.rev.entityType) {
          case this.$constants.revTypes.improvementIdea:
            await this.iiCancelValidationRequest(requestPayload)
            break
          case this.$constants.revTypes.problemSolving:
            await this.psCancelValidationRequest(requestPayload)
            break
          default:
            await this.qaCancelValidationRequest(requestPayload)
        }
        this.trackValidationsActions({
          action: 'drawer-cancelValidation',
          revId: this.rev.id,
          revType: this.revType,
        })

        this.showSuccess(this.$t('rev.validation_request_canceled'))
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.$emit('cancelRequestValidation')
        this.$emit('close')
      }
    },
  },
}
</script>
